import { useState, useEffect } from 'react';
import { Layout } from '../layouts/Layout';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { DatePickerCard } from '../components/DatePickerCard';
import DeviceList from '../components/DeviceList';
import SleepChart from '../components/SleepChart';
import SleepTrackingChart from '../components/SleepTrackingChart';

import { api } from '../utils/api';
import { getTodayDate } from '../utils/dateUtils';
import type { Device, SleepData } from '../types';

export default function SleepMonitoring() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [sleepByDevice, setSleepByDevice] = useState<SleepData | null>(null);
  const [allSleepData, setAllSleepData] = useState<SleepData[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(getTodayDate());

  // Dispatch initial date on mount to ensure all components know the selected date
  useEffect(() => {
    console.log('SleepMonitoring - Initial date set to:', startDate);
    
    // Dispatch initial date event after a short delay to ensure components are mounted
    const timer = setTimeout(() => {
      console.log('SleepMonitoring - Dispatching initial date event:', startDate);
      const event = new CustomEvent('datepicker-range-end', {
        detail: startDate
      });
      document.dispatchEvent(event);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Fetch devices on mount
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const userCompany = localStorage.getItem('userCompany') || '';
        const fetchedDevices = await api.getDevicesByCompany(userCompany);
        setDevices(fetchedDevices);

        // Auto-select first device if available
        if (fetchedDevices.length > 0 && fetchedDevices[0]?.id) {
          setSelectedDeviceId(fetchedDevices[0].id);
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
      }
    };

    fetchDevices();
  }, []);

  // Fetch sleep data when device or date changes
  useEffect(() => {
    const fetchSleepData = async () => {
      if (!selectedDeviceId || !startDate) {
        console.log('SleepMonitoring - Skipping sleep data fetch - missing deviceId or date', { selectedDeviceId, startDate });
        return;
      }

      try {
        console.log('SleepMonitoring - Fetching sleep data for device:', selectedDeviceId, 'date:', startDate);
        const data = await api.getDeviceSleepData(selectedDeviceId, startDate);
        console.log('SleepMonitoring - Sleep data received:', data);
        setSleepByDevice(data);
        
        // Dispatch event to notify other components
        const event = new CustomEvent('sleep-data-update', {
          detail: data
        });
        document.dispatchEvent(event);
      } catch (error) {
        console.error('SleepMonitoring - Error fetching sleep data:', error);
        setSleepByDevice(null);
      }
    };

    // Only fetch if we have both deviceId and date
    if (selectedDeviceId && startDate) {
      fetchSleepData();
    }
  }, [selectedDeviceId, startDate]);

  // Fetch all sleep data for device list using optimized endpoint
  useEffect(() => {
    const fetchAllSleepData = async () => {
      if (devices.length === 0) {
        return;
      }

      try {
        // Use optimized summary-health endpoint instead of individual calls
        const response = await api.getSummaryHealth({
          date: startDate,
          limit: 100
        });

        if (response.success && response.data) {
          const sleepDataArray: SleepData[] = response.data.devices.map((deviceSummary: any) => ({
            deviceId: deviceSummary.deviceId,
            device_id: deviceSummary.deviceId,
            date: startDate,
            sleepTime: deviceSummary.sleepData?.sleepTime || 0,
            sleepQuality: deviceSummary.sleepData?.sleepQuality ? deviceSummary.sleepData.sleepQuality / 100 : 0,
            sleepTotalTime: deviceSummary.sleepData?.sleepTime || 0,
            originalSleepTime: deviceSummary.sleepData?.sleepTime || 0,
            heartRate: deviceSummary.sleepData?.heartRate || { min: 0, max: 0 },
            bloodOxygen: deviceSummary.sleepData?.bloodOxygen || { min: 0, max: 0 },
            sleepLogs: deviceSummary.sleepData?.sleepLogs || [],
            startTime: deviceSummary.sleepData?.startTime,
            endTime: deviceSummary.sleepData?.endTime
          }));
          
          setAllSleepData(sleepDataArray);
        }
      } catch (error) {
        console.error('Error fetching all sleep data:', error);
        setAllSleepData([]);
      }
    };

    fetchAllSleepData();
  }, [devices, startDate]);

  // Handle device selection from DeviceList
  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    
    // Dispatch custom event for other components
    const event = new CustomEvent('device-select', {
      detail: { deviceId }
    });
    document.dispatchEvent(event);
  };

  useEffect(() => {
    const handleDeviceSelectEvent = (e: CustomEvent) => {
      if (e.detail?.deviceId) {
        const newDeviceId = e.detail.deviceId;
        console.log('SleepMonitoring - Device selected:', newDeviceId, 'Current date:', startDate);
        setSelectedDeviceId(newDeviceId);
        
        // Immediately fetch sleep data for the newly selected device
        const fetchData = async () => {
          try {
            console.log('SleepMonitoring - Fetching sleep data for device:', newDeviceId, 'date:', startDate);
            const data = await api.getDeviceSleepData(newDeviceId, startDate);
            console.log('SleepMonitoring - Sleep data received:', data);
            setSleepByDevice(data);
            
            // Dispatch event to notify other components
            const event = new CustomEvent('sleep-data-update', {
              detail: data
            });
            document.dispatchEvent(event);
          } catch (error) {
            console.error('SleepMonitoring - Error fetching sleep data:', error);
            setSleepByDevice(null);
          }
        };
        
        fetchData();
      }
    };

    document.addEventListener('device-select', handleDeviceSelectEvent as EventListener);

    return () => {
      document.removeEventListener('device-select', handleDeviceSelectEvent as EventListener);
    };
  }, [startDate]); // Add startDate as dependency

  // Handle date changes from DatePickerCard
  const handleDateChange = (date: string) => {
    setStartDate(date);
    
    // Dispatch custom event for other components
    const event = new CustomEvent('datepicker-range-end', {
      detail: date
    });
    document.dispatchEvent(event);
  };

  return (
    <Layout>
      <Sidebar />
      <Header title="Sleep Monitoring" />

      <main className="ml-0 md:ml-64 pt-20 p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Date Picker Card */}
          <DatePickerCard onDateChange={handleDateChange} initialDate={startDate} />

          {/* Users and Health Tracking */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <DeviceList
              devices={devices}
              sleepData={allSleepData}
              scheduleData={[]}
              className="md:col-span-6 lg:col-span-5 xl:col-span-4"
              onDeviceSelect={handleDeviceSelect}
            />
            <SleepChart
              sleepData={sleepByDevice}
              devices={devices}
              className="md:col-span-6 lg:col-span-7 xl:col-span-8"
            />
          </div>

          {/* Sleep Monitoring and Summary */}
          <div className="grid grid-cols-1 gap-6">
            <SleepTrackingChart
              sleepData={sleepByDevice}
              selectedDeviceId={selectedDeviceId}
              className="md:flex-1"
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}
